import React, { useState, useMemo } from 'react';
import { theme, Affix, Tooltip, Badge } from 'antd';
import RightDrawer from '../../../components/Drawers/RightDrawer';
import HeatMapLayout from '../../../components/HeatMapLayout/HeatMapLayout';
import { transformHeatmap } from '../../../utils';
import type { VinInspectionItem } from '../inspectionTypes';
import { getUniqueDamageCount } from '../inspectionUtils';
import { isDamageDeleted } from '../utils/damageAnnotationUtils';

const DRAWER_WIDTH = 800;

interface DefectSideBarProps {
  loading: boolean;
  heatMapData: VinInspectionItem[];
}

function DefectSideBar({ loading, heatMapData }: DefectSideBarProps) {
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);

  // Unlike getUniqueDamageCount (single inspection), this dedupes damage ids
  // across a whole list of inspections, falling back to summing damageCount
  // only for inspections that don't have a damages array at all.
  const getUniqueDamageCountAcrossInspections = (inspections: VinInspectionItem[]): number => {
    const uniqueIds = new Set<string | number>();
    let fallbackCount = 0;

    inspections.forEach(inspection => {
      const damages = inspection?.damages;
      if (Array.isArray(damages)) {
        damages.forEach(damage => {
          if (isDamageDeleted(damage)) {
            return;
          }

          const id = damage?.id;
          if (id !== undefined && id !== null && id !== '') {
            uniqueIds.add(id);
          }
        });
        return;
      }

      fallbackCount += Number(inspection?.damageCount || 0);
    });

    return uniqueIds.size + fallbackCount;
  };

  const toggleDrawer = () => {
    setOpen(prev => !prev);
  };
  // Filter and search logic
  const filteredHeatMapData = useMemo(() => {
    if (!heatMapData.length) return [];

    let filtered = [...heatMapData];

    return filtered;
  }, [heatMapData]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalDamages = getUniqueDamageCountAcrossInspections(heatMapData);
    const affectedParts = heatMapData.length;
    const highSeverityParts = heatMapData.filter(item => {
      const uniqueCount = Array.isArray(item?.damages) ? getUniqueDamageCount(item.damages) : Number(item?.damageCount || 0);
      return uniqueCount > 5;
    }).length;

    return { totalDamages, affectedParts, highSeverityParts };
  }, [heatMapData]);

  return (
    <>
      <Affix>
        <div
          style={{
            position: 'fixed',
            right: open ? DRAWER_WIDTH : 0,
            top: '50%',
            transform: 'translateY(-50%)',
            transition: 'right 0.3s ease',
            zIndex: 999,
          }}>
          <Tooltip title={`${statistics.totalDamages} defects found`} placement="left">
            <div
              onClick={toggleDrawer}
              className="bg-red-600 hover:bg-red-700 
                   text-white cursor-pointer
                   rounded-l-lg shadow-xl
                   w-10 py-2
                   flex flex-col items-center gap-1">
              {/* Count Badge */}
              <Badge count={statistics.totalDamages} size="small" style={{ backgroundColor: '#222831', color: '#fff' }}></Badge>
              {/* Vertical Text */}
              <span className="[writing-mode:vertical-rl] rotate-180 text-xs tracking-[1px]">DEFECTS</span>
              {/* Arrow */}
              <span className="text-lg leading-none">{open ? '>' : '<'}</span>
            </div>
          </Tooltip>
        </div>
      </Affix>

      <RightDrawer
        size={DRAWER_WIDTH}
        open={open}
        closable={false}
        onClose={() => setOpen(false)}
        mask={false}
        rootClassName="defect-sidebar-drawer"
        className="rounded-l-2xl"
        style={{ borderLeft: `3px solid ${token.colorPrimary}`, borderRadius: '5px' }}
        styles={{ body: { padding: 0 } }}>
        <div className="flex h-full items-center justify-center p-6">
          <div className="flex h-full w-full max-w-[720px] items-center justify-center rounded-lg border bg-gray-50">
            <HeatMapLayout data={transformHeatmap(filteredHeatMapData)} divident={1} loading={loading} />
          </div>
        </div>
      </RightDrawer>
    </>
  );
}

export default DefectSideBar;
